import React, { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import { useNavigate } from "react-router-dom";
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY, {
  locale: 'en' // Explicitly set locale
});

const API_URL = process.env.REACT_APP_API_URL;

const Checkout = () => {
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [formData, setFormData] = useState({
    firstName: "", lastName: "", phoneNumber: "", email: "", address: "",
    city: "", state: "", country: "", pincode: "", landmark: ""
  });
  const [cart, setCart] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const navigate = useNavigate();

  const isLoggedIn = () => !!localStorage.getItem("token");

  useEffect(() => {
    if (isLoggedIn()) {
      fetchAddresses();
      fetchCart();
    } else {
      // Enable guest checkout by default
      const guestCart = JSON.parse(localStorage.getItem("cart")) || [];
      const guestTotal = guestCart.reduce((sum, item) => sum + item.price * item.quantity, 0);
      setCart(guestCart);
      setTotalPrice(guestTotal);

      const savedAddress = JSON.parse(localStorage.getItem("guestAddress"));
      if (savedAddress) {
        setAddresses([savedAddress]);
        setSelectedAddress(savedAddress);
        setFormData(savedAddress); // Populate form with saved address for guests
      }
    }
  }, []);

  const fetchAddresses = async () => {
    const token = localStorage.getItem("token");
    const userId = sessionStorage.getItem("userid");
    if (!token) return;

    try {
      setIsLoading(true);
      const res = await axios.post(
        `${API_URL}/api/address/my-addresses`,
        { userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAddresses(res.data);
    } catch {
      setError("Failed to fetch addresses. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCart = async () => {
    const token = localStorage.getItem("token");
    const userId = sessionStorage.getItem("userid");
    if (!token) return;

    try {
      setIsLoading(true);
      const res = await axios.get(`${API_URL}/api/cart/getusercart/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCart(res.data.cart);
      setTotalPrice(res.data.totalPrice);
    } catch {
      setError("Failed to fetch cart details. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (["firstName", "lastName", "city", "state", "country"].includes(name)) {
      updatedValue = value.replace(/[^a-zA-Z\s]/g, "");
    }
    if (name === "phoneNumber") {
      updatedValue = value.replace(/\D/g, "").slice(0, 10);
    }
    if (name === "pincode") {
      updatedValue = value.replace(/\D/g, "").slice(0, 6);
    }

    setFormData({ ...formData, [name]: updatedValue });
  };

  const validateAddress = () => {
    const required = ["firstName", "lastName", "phoneNumber", "email", "address", "city", "state", "country", "pincode"];
    for (let field of required) {
      if (!formData[field]) {
        setError(`Please fill in the ${field} field.`);
        return false;
      }
    }
    if (!/^\d{10}$/.test(formData.phoneNumber)) {
      setError("Phone number must be 10 digits.");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
      setError("Invalid email format.");
      return false;
    }
    if (!/^\d{6}$/.test(formData.pincode)) {
      setError("Pincode must be 6 digits.");
      return false;
    }

    setError("");
    return true;
  };

  const handleAddOrEdit = async () => {
    if (!validateAddress()) return;
    const token = localStorage.getItem("token");
    const userId = sessionStorage.getItem("userid");

    if (isLoggedIn()) {
      try {
        const url = editingId
          ? `${API_URL}/api/address/edit/${editingId}`
          : `${API_URL}/api/address/add`;
        const method = editingId ? "put" : "post";

        await axios({
          method,
          url,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          data: { ...formData, userId },
        });

        alert(editingId ? "Address updated." : "Address added.");
        fetchAddresses();
        setFormData({ firstName: "", lastName: "", phoneNumber: "", email: "", address: "", city: "", state: "", country: "", pincode: "", landmark: "" });
        setEditingId(null);
      } catch {
        setError("Failed to save address.");
      }
    } else {
      localStorage.setItem("guestAddress", JSON.stringify(formData));
      setAddresses([formData]);
      setSelectedAddress(formData);
      alert("Guest address saved.");
    }
  };

  const handleDelete = async (addressId) => {
    const token = localStorage.getItem("token");
    if (!token) return;

    if (!window.confirm("Are you sure you want to delete this address?")) return;

    try {
      await axios.delete(`${API_URL}/api/address/delete/${addressId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert("Address deleted successfully.");
      fetchAddresses();
    } catch {
      setError("Failed to delete address.");
    }
  };

  // New function to handle selecting an address and populate the form
  const handleSelectAddress = (addr) => {
  setSelectedAddress(addr);
  setFormData({
    firstName: addr.firstName || "",
    lastName: addr.lastName || "",
    phoneNumber: addr.phoneNumber || "",
    email: addr.email || "",
    address: addr.address || "",
    city: addr.city || "",
    state: addr.state || "",
    country: addr.country || "",
    pincode: addr.pincode || "",
    landmark: addr.landmark || "",
  });
  setEditingId(addr.id); // only track the ID internally, not in the form
};

  const handlePayment = async () => {
    if (!selectedAddress) return setError("Please select or add an address.");

    const addressData = {
      fullName: `${selectedAddress.firstName} ${selectedAddress.lastName}`,
      phoneNumber: selectedAddress.phoneNumber,
      email: selectedAddress.email,
      addressLine: selectedAddress.address,
      city: selectedAddress.city,
      state: selectedAddress.state,
      pincode: selectedAddress.pincode,
      landmark: selectedAddress.landmark || "",
    };

    try {
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error("Stripe failed to initialize");
      }
      if (isLoggedIn()) {
        const token = localStorage.getItem("token");
        const userId = sessionStorage.getItem("userid");

        const { data } = await axios.post(
          `${API_URL}/api/orders/create?userId=${userId}`,
          addressData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const redirectUrl = new URL(data.url);
        redirectUrl.searchParams.append("auth_token", token);
        redirectUrl.searchParams.append("userId", userId);

        setTimeout(() => {
          window.location.href = redirectUrl.toString();
        }, 3000);
      } else {
        // Guest payment flow
        const guestOrderData = {
          address: addressData,
          cartItems: cart,
          total: totalPrice,
        };

        const { data } = await axios.post(
          `${API_URL}/api/guest-orders/create`,
          guestOrderData
        );

        setTimeout(() => {
          window.location.href = data.url;
        }, 3000);
      }
    } catch {
      setError("There was an issue initiating payment.");
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("session_id")) {
      const sessionId = params.get("session_id");
      const userId = sessionStorage.getItem("userid");
      const token = localStorage.getItem("token");

      navigate(`/payment-success?session_id=${sessionId}&userId=${userId}&auth_token=${token}`);
    }
  }, [navigate]);

  // Function to clear form and reset editing state
  const handleClearForm = () => {
    setFormData({
      firstName: "", lastName: "", phoneNumber: "", email: "", address: "",
      city: "", state: "", country: "", pincode: "", landmark: ""
    });
    setEditingId(null);
  };

  return (
    <div className="flex flex-col md:flex-row gap-6 p-6 max-w-7xl mx-auto">
      {/* Address list */}
      <div className="w-full md:w-1/2">
        <h2 className="text-xl font-bold mb-4">Your Addresses</h2>
        {error && <p className="text-red-500">{error}</p>}
        {isLoading ? <p>Loading...</p> : addresses.map((addr, i) => (
          <div key={i} className={`p-4 border rounded mb-3 ${selectedAddress?.email === addr.email ? "border-green-600 bg-green-50" : "border-gray-300"}`}>
            <p><strong>{addr.firstName} {addr.lastName}</strong> ({addr.phoneNumber})</p>
            <p>{addr.address}, {addr.city}, {addr.state}, {addr.country} - {addr.pincode}</p>
            <p>Email: {addr.email}</p>
            <p>Landmark: {addr.landmark}</p>
            <div className="mt-2 flex gap-2">
              <button onClick={() => handleSelectAddress(addr)} className="text-sm bg-blue-500 text-white px-2 py-1 rounded">Select & Edit</button>
              {isLoggedIn() && (
                <button onClick={() => handleDelete(addr.id)} className="text-sm bg-red-500 text-white px-2 py-1 rounded">Delete</button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Address form and payment */}
      <div className="w-full md:w-1/2">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{editingId ? "Edit Address" : "Add New Address"}</h2>
          {editingId && (
            <button 
              onClick={handleClearForm} 
              className="text-sm bg-gray-500 text-white px-3 py-1 rounded"
            >
              Add New Instead
            </button>
          )}
        </div>
        <form className="grid grid-cols-1 gap-4">
          {Object.keys(formData).map((field) => (
            field === "address" ? (
              <textarea key={field} name={field} value={formData[field]} onChange={handleChange} placeholder={field} className={`border p-2 rounded ${error.toLowerCase().includes(field.toLowerCase()) ? "border-red-500" : "border-gray-300"}`} />
            ) : (
              <input key={field} name={field} value={formData[field]} onChange={handleChange} placeholder={field} className={`border p-2 rounded ${error.toLowerCase().includes(field.toLowerCase()) ? "border-red-500" : "border-gray-300"}`} />
            )
          ))}
          <button type="button" onClick={handleAddOrEdit} className="bg-green-600 text-white p-2 rounded mt-2">
            {editingId ? "Update Address" : "Add Address"}
          </button>
        </form>

        <button
          onClick={handlePayment}
          className={`bg-indigo-600 text-white mt-6 w-full p-3 rounded-lg ${!selectedAddress ? "opacity-50 cursor-not-allowed" : ""}`}
          disabled={!selectedAddress}
        >
          Proceed to Pay ₹{totalPrice}
        </button>
      </div>
    </div>
  );
};

export default Checkout;